// @flow
import React, { useState, Fragment, type Node } from 'react';
import classnames from 'classnames';
import { UnmountClosed } from 'react-collapse';

import { ICONS } from 'Components/utils/icon/icons';

import { DropZone } from 'Components/frameworks/drop-zone/DropZone.jsx';
import { Icon } from 'Components/frameworks/icon/Icon.jsx';
import { ProductCondensed } from 'Components/widgets/product-condensed/ProductCondensed.jsx';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer.jsx';
import { SearchCompareButton } from 'Components/widgets/facet-search-results/search-compare-button/SearchCompareButton.jsx';
import { StripButton } from 'Components/frameworks/strip-button/StripButton.jsx';

type Props = {
  buttonLabel: string,
  cancelLabel: string,
  children?: Node,
  className?: string,
  collapseLabel?: string,
  expandLabel?: string,
  comparisonClick: () => void,
  comparisonToggle: () => void,
  comparisonUrl: string,
  dropzoneLabel?: string,
  isComparisonActive?: boolean,
  isComparisonSubmitable?: boolean,
  isProductsTopInViewport?: boolean,
  isProductsBottomInViewport?: boolean,
  mandatoryMessage?: string,
  resultsCount?: number,
  selectedItems?: Array<*>,
  selectedItemsMax: number,
  toggleProductSelectedForComparison: ?(string) => *,
};

export function SearchCompareNarrow(props: Props) {
  const {
    buttonLabel,
    cancelLabel,
    collapseLabel,
    expandLabel,
    children,
    className,
    comparisonClick,
    comparisonToggle,
    comparisonUrl,
    dropzoneLabel,
    isComparisonActive,
    isComparisonSubmitable,
    isProductsTopInViewport,
    isProductsBottomInViewport,
    mandatoryMessage,
    resultsCount,
    selectedItems = [],
    selectedItemsMax = 3,
    toggleProductSelectedForComparison,
  } = props;

  const [isExpanded, setExpanded] = useState(false);

  const selectedItemsCount = selectedItems.length;

  return (
    <Fragment>
      <SiteContainer className={className}>{children}</SiteContainer>
      {resultsCount &&
        resultsCount > 0 && (
          <section
            className={classnames('bg-white border-t-1 border-gray-light bottom-0 w-full z-3', {
              fixed: isProductsTopInViewport,
              absolute: isProductsBottomInViewport,
            })}
          >
            {!!selectedItemsCount && (
              <div className="absolute bottom-full flex justify-center w-full">
                <StripButton
                  className="font-bold text-center text-blue text-sm underline"
                  height={30}
                  onClick={() => setExpanded(!isExpanded)}
                >
                  {isExpanded ? collapseLabel : expandLabel}
                  <Icon
                    className="rtl:ml-2 flex-shrink-0"
                    name={isExpanded ? ICONS.SELECT : ICONS.SELECT_UP}
                    width="0.625rem"
                  />
                </StripButton>
              </div>
            )}
            <UnmountClosed isOpened={isExpanded}>
              <SiteContainer>
                <div className="pt-5 pb-1">
                  {Array(selectedItemsMax)
                    .fill()
                    .map((_, idx) => {
                      const product = selectedItems[idx];
                      const productId = product && product.id;

                      return (
                        <div className="mb-5" key={productId || idx}>
                          <DropZone
                            label={dropzoneLabel}
                            number={idx + 1}
                            onClear={
                              productId && toggleProductSelectedForComparison
                                ? () => {
                                    toggleProductSelectedForComparison(productId);
                                    if (selectedItemsCount - 1 === 0) setExpanded(!isExpanded);
                                  }
                                : undefined
                            }
                          >
                            {product && <ProductCondensed {...product} />}
                          </DropZone>
                        </div>
                      );
                    })}
                </div>
              </SiteContainer>
            </UnmountClosed>
            <SiteContainer>
              <div className="flex items-center justify-between py-3">
                {isComparisonActive && (
                  <button
                    className="reset-button font-bold text-base rtl:mr-5"
                    onClick={() => {
                      comparisonToggle();
                      setExpanded(false);
                    }}
                    type="button"
                  >
                    {cancelLabel}
                  </button>
                )}
                <SearchCompareButton
                  className="flex-1"
                  comparisonToggle={comparisonToggle}
                  comparisonClick={comparisonClick}
                  comparisonUrl={comparisonUrl}
                  isComparisonActive={isComparisonActive}
                  isComparisonSubmitable={isComparisonSubmitable}
                  label={buttonLabel}
                  mandatoryMessage={mandatoryMessage}
                  selectedItemsCount={selectedItemsCount}
                  selectedItemsMax={selectedItemsMax}
                  style={{ minHeight: 40 }}
                />
              </div>
            </SiteContainer>
          </section>
        )}
    </Fragment>
  );
}
